import AddIcon from '@mui/icons-material/Add';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { MouseEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRootCreateMutation, useRootDestroyMutation, useRootListQuery } from '../api/root.js';
import { useSnapshotCreateMutation } from '../api/snapshot.js';

export function RootsView() {
  const { projectId } = useParams();
  if (!projectId) throw new Error('Missing parameter');

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const transparencySnapshot = useSnapshotCreateMutation('transparency');
  const rootDestroy = useRootDestroyMutation(projectId);
  const rootCreate = useRootCreateMutation(projectId);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [hostname, setHostname] = useState('');
  const [hostnameError, setHostnameError] = useState<string | undefined>(undefined);
  const [confirmingRootId, setConfirmingRootId] = useState<string | null>(null);
  const handleCreateClick = () => {
    setHostname('');
    setHostnameError(undefined);
    setIsCreateOpen(true);
  };

  const handleRowClick = (rootId: string) => {
    navigate(`/projects/${projectId}/roots/${rootId}`);
  };

  const handleDeleteClick = (rootId: string) => {
    setConfirmingRootId(rootId);
  };

  const handleSnapshotClick = (rootId: string, event: MouseEvent) => {
    event.stopPropagation();
    transparencySnapshot.mutate(
      { projectId, resourceId: rootId },
      {
        onSuccess: () => enqueueSnackbar('Transparency snapshot queued', { variant: 'success' }),
        onError: (err) =>
          enqueueSnackbar(`Failed to queue snapshot: ${err instanceof Error ? err.message : String(err)}`, {
            variant: 'error'
          })
      }
    );
  };

  const creatingRootId = transparencySnapshot.variables?.resourceId;
  const deletingRootId = rootDestroy.variables?.rootId;
  const creating = rootCreate.isLoading;
  const handleConfirmDelete = () => {
    if (!confirmingRootId) return;
    rootDestroy.mutate(
      { rootId: confirmingRootId },
      {
        onSuccess: () => enqueueSnackbar('Root deleted', { variant: 'success' }),
        onError: (err) =>
          enqueueSnackbar(`Failed to delete root: ${err instanceof Error ? err.message : String(err)}`, {
            variant: 'error'
          }),
        onSettled: () => setConfirmingRootId(null)
      }
    );
  };

  const { data: roots, isLoading, isError } = useRootListQuery(projectId);
  if (isLoading) {
    return (
      <Backdrop open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isError || !roots) {
    return <Alert severity="error">Unable to load roots. Please try again later.</Alert>;
  }

  return (
    <Paper elevation={1} sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Roots</Typography>
        <Button variant="contained" color="primary" onClick={handleCreateClick} startIcon={<AddIcon />}>
          {creating ? 'Creating...' : 'Create Root'}
        </Button>
      </Box>
      <Dialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Create Root</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Hostname"
            fullWidth
            value={hostname}
            error={!!hostnameError}
            helperText={hostnameError}
            onChange={(e) => {
              setHostname(e.target.value);
              if (hostnameError) setHostnameError(undefined);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              const value = hostname.trim();
              if (!value) {
                setHostnameError('Hostname is required');
                return;
              }

              rootCreate.mutate(value, {
                onSuccess: () => {
                  enqueueSnackbar('Root created', { variant: 'success' });
                  setIsCreateOpen(false);
                },
                onError: (err) => setHostnameError(err instanceof Error ? err.message : String(err))
              });
            }}
            disabled={creating}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!confirmingRootId} onClose={() => setConfirmingRootId(null)}>
        <DialogTitle>Delete Root</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete root {confirmingRootId}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingRootId(null)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete} disabled={rootDestroy.isLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Hostname</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roots.map((root) => (
              <TableRow
                key={root.id}
                hover
                onClick={() => handleRowClick(root.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{root.id}</TableCell>
                <TableCell>{root.hostname}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Snapshot" arrow>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Snapshot"
                        disabled={transparencySnapshot.isLoading && creatingRootId === root.id}
                        onClick={(event) => handleSnapshotClick(root.id, event)}
                      >
                        <CameraAltIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Delete"
                        color="error"
                        disabled={rootDestroy.isLoading && deletingRootId === root.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(root.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
